import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import {
  CMSContentType,
  CMSFieldDefinition,
  CMSListFilters,
  CMSMenuLink,
  CMSObject,
  CMSPageField,
  CMSRevision,
  CMSSlugHistory,
  CMSTag,
  CMSTemplate,
  normalizeCMSObject,
} from '@/client/types/cms';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

const CMS = '/api/admin/cms';

export const cmsKeys = {
  all: ['cms-admin'] as const,
  lists: () => [...cmsKeys.all, 'list'] as const,
  list: (page: number, filters: object) => [...cmsKeys.lists(), page, filters] as const,
  detail: (cmsoid: number, rel?: boolean) => [...cmsKeys.all, 'detail', cmsoid, !!rel] as const,
  history: (cmsoid: number) => [...cmsKeys.all, 'history', cmsoid] as const,
  slugHistory: (cmsoid: number) => [...cmsKeys.all, 'slug-history', cmsoid] as const,
  pageFields: (cmsoid: number) => [...cmsKeys.all, 'page-fields', cmsoid] as const,
  pageTags: (cmsoid: number) => [...cmsKeys.all, 'page-tags', cmsoid] as const,
  pageMenus: (cmsoid: number) => [...cmsKeys.all, 'page-menus', cmsoid] as const,
  tree: (q?: object) => [...cmsKeys.all, 'tree', q ?? {}] as const,
  contentTypes: (detailed?: boolean) => [...cmsKeys.all, 'content-types', !!detailed] as const,
  templates: (filters?: object) => [...cmsKeys.all, 'templates', filters ?? {}] as const,
  fieldDefs: (ctype?: string) => [...cmsKeys.all, 'field-defs', ctype ?? ''] as const,
  tags: (q?: object) => [...cmsKeys.all, 'tags', q ?? {}] as const,
};

const qs = (params: Record<string, unknown>) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : '';
};

/** ff-api wraps payloads as `{ status, data }`. List endpoints put the array in `data`. */
const unwrapEnvelope = (raw: any): any => {
  if (raw != null && typeof raw === 'object' && 'status' in raw && 'data' in raw) {
    return raw.data;
  }
  return raw;
};

const unwrapList = (raw: any): any[] => {
  const payload = unwrapEnvelope(raw);
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.cms_objects)) return payload.cms_objects;
  if (Array.isArray(payload?.pages)) return payload.pages;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.templates)) return payload.templates;
  if (Array.isArray(payload?.fields)) return payload.fields;
  if (Array.isArray(payload?.tags)) return payload.tags;
  if (Array.isArray(payload?.menus)) return payload.menus;
  // Legacy: envelope already peeled by caller, or top-level collection keys
  if (Array.isArray(raw?.cms_objects)) return raw.cms_objects;
  if (Array.isArray(raw?.data)) return raw.data;
  return [];
};

// ─── Pages list / detail ──────────────────────────────────────────────────────

export interface CMSListResult {
  items: CMSObject[];
  total: number;
  limit: number;
  offset: number;
  /**
   * True only when the catalog total is unknown (API omitted `pagination.total`).
   * Matches Pagination's `hasMore` contract: open-ended "10+" footer, not
   * "there is a next page when total is known".
   */
  hasMore?: boolean;
}

/** Prefer a real catalog total; never treat page `count` as catalog size. */
const coerceCMSPagination = (raw: any): { total?: number; has_more?: boolean } | undefined => {
  const candidates = [
    raw?.pagination,
    raw?.data?.pagination,
    raw?.data?.data?.pagination,
    typeof raw?.data === 'object' && raw?.data && !Array.isArray(raw.data) ? raw.data : null,
    raw,
  ];
  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) continue;
    const totalRaw =
      candidate.total ??
      candidate.total_count ??
      candidate.total_pages_count;
    // Ignore page-size `count` — that is rows on this page, not catalog size.
    if (totalRaw != null && totalRaw !== '' && Number.isFinite(Number(totalRaw))) {
      return { total: Number(totalRaw), has_more: candidate.has_more };
    }
    if (candidate.pagination && typeof candidate.pagination === 'object') {
      const nested = coerceCMSPagination({ pagination: candidate.pagination });
      if (nested?.total != null) return nested;
    }
  }
  return undefined;
};

const useCMSListFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (page = 1, filters: CMSListFilters = {}): Promise<CMSListResult> => {
    const limit = filters.limit ?? 25;
    const offset = filters.offset ?? (page - 1) * limit;
    const params: Record<string, unknown> = { limit, offset };

    if (filters.ctype) params.ctype = filters.ctype;
    if (filters.locale) params.locale = filters.locale;
    if (filters.country_iso2) params.country_iso2 = filters.country_iso2;
    if (filters.q) params.q = filters.q;

    if (filters.status === 'published' || filters.only_published) params.only_published = true;
    if (filters.status === 'deleted' || filters.include_deleted) params.include_deleted = true;

    const raw = await request<any>(`${CMS}${qs(params)}`);
    const payload = unwrapEnvelope(raw);
    let items = unwrapList(raw).map(normalizeCMSObject);

    // Client-side draft filter when API returns mixed rows
    if (filters.status === 'draft') {
      items = items.filter((p) => !p.published && !p.deleted && p.active);
    } else if (filters.status === 'inactive') {
      items = items.filter((p) => !p.active && !p.deleted);
    } else if (filters.status === 'expired') {
      items = items.filter((p) => p.status === 'expired');
    }

    const coerced = coerceCMSPagination(payload) ?? coerceCMSPagination(raw);
    const hasReportedTotal = coerced?.total != null && Number.isFinite(coerced.total);
    const pageFull = items.length >= limit;
    // Pagination.hasMore means "total is a lower bound", not "another page exists".
    const hasMore = !hasReportedTotal && pageFull;
    const total = hasReportedTotal
      ? coerced!.total!
      : offset + items.length;

    if (process.env.NODE_ENV === 'development' && items.length === 0) {
      const shape = Array.isArray(payload)
        ? `data:array[${payload.length}]`
        : payload && typeof payload === 'object'
          ? `data:object{${Object.keys(payload).slice(0, 8).join(',')}}`
          : `data:${typeof payload}`;
      console.debug(`[CMS list] empty after unwrap — ${shape}`, { params, status: raw?.status });
    }

    return { items, total, limit, offset, hasMore };
  }, 'Failed to fetch CMS pages');
};

export const useCMSListQuery = (
  page = 1,
  filters: CMSListFilters = {},
  opts?: { enabled?: boolean },
) => {
  const queryFn = useCMSListFn();
  return useQuery({
    queryKey: cmsKeys.list(page, filters),
    queryFn: () => queryFn(page, filters),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    enabled: opts?.enabled !== false,
  });
};

const useCMSDetailFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (cmsoid: number, includeRelations = true): Promise<CMSObject> => {
    const raw = await request<any>(
      `${CMS}/${cmsoid}${qs({ include_relations: includeRelations })}`,
    );
    const data = raw?.data ?? raw;
    return normalizeCMSObject(data);
  }, 'Failed to fetch CMS page');
};

export const useCMSDetailQuery = (cmsoid: number | null, includeRelations = true) => {
  const queryFn = useCMSDetailFn();
  return useQuery({
    queryKey: cmsKeys.detail(cmsoid ?? 0, includeRelations),
    queryFn: () => queryFn(cmsoid!, includeRelations),
    enabled: typeof cmsoid === 'number' && cmsoid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useCMSHistoryQuery = (cmsoid: number | null) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: cmsKeys.history(cmsoid ?? 0),
    queryFn: withErrorHandling(async () => {
      const raw = await request<any>(`${CMS}/${cmsoid}/history`);
      return unwrapList(raw) as CMSRevision[];
    }, 'Failed to fetch revisions'),
    enabled: typeof cmsoid === 'number' && cmsoid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useCMSSlugHistoryQuery = (cmsoid: number | null) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: cmsKeys.slugHistory(cmsoid ?? 0),
    queryFn: withErrorHandling(async () => {
      const raw = await request<any>(`${CMS}/${cmsoid}/slug-history`);
      const payload = unwrapEnvelope(raw);
      if (Array.isArray(payload?.slug_history)) return payload.slug_history as CMSSlugHistory[];
      if (Array.isArray(payload)) return payload as CMSSlugHistory[];
      return unwrapList(raw) as CMSSlugHistory[];
    }, 'Failed to fetch slug history'),
    enabled: typeof cmsoid === 'number' && cmsoid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useCMSPageFieldsQuery = (cmsoid: number | null) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: cmsKeys.pageFields(cmsoid ?? 0),
    queryFn: withErrorHandling(async () => {
      const raw = await request<any>(`${CMS}/${cmsoid}/fields`);
      return (raw?.fields ?? unwrapList(raw)) as CMSPageField[];
    }, 'Failed to fetch page fields'),
    enabled: typeof cmsoid === 'number' && cmsoid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useCMSPageTagsQuery = (cmsoid: number | null) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: cmsKeys.pageTags(cmsoid ?? 0),
    queryFn: withErrorHandling(async () => {
      const raw = await request<any>(`${CMS}/${cmsoid}/tags`);
      return (raw?.tags ?? unwrapList(raw)) as CMSTag[];
    }, 'Failed to fetch page tags'),
    enabled: typeof cmsoid === 'number' && cmsoid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useCMSPageMenusQuery = (cmsoid: number | null) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: cmsKeys.pageMenus(cmsoid ?? 0),
    queryFn: withErrorHandling(async () => {
      const raw = await request<any>(`${CMS}/${cmsoid}/menus`);
      const payload = unwrapEnvelope(raw);
      if (Array.isArray(payload?.menus)) return payload.menus as CMSMenuLink[];
      return unwrapList(raw) as CMSMenuLink[];
    }, 'Failed to fetch page menu links'),
    enabled: typeof cmsoid === 'number' && cmsoid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useCMSTreeQuery = (filters: { ctype?: string; locale?: string } = {}, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: cmsKeys.tree(filters),
    queryFn: withErrorHandling(async () => {
      const raw = await request<any>(`${CMS}/tree${qs(filters)}`);
      return unwrapList(raw).map(normalizeCMSObject);
    }, 'Failed to fetch CMS tree'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useCMSCheckQuery = (title: string | null) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: [...cmsKeys.all, 'check', title ?? ''] as const,
    queryFn: withErrorHandling(async () => {
      return request<any>(`${CMS}/check${qs({ title })}`);
    }, 'Failed to check title availability'),
    enabled: !!title && title.trim().length > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

// ─── Content types ────────────────────────────────────────────────────────────

export const useCMSContentTypesQuery = (detailed = false, includeInactive = false) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: cmsKeys.contentTypes(detailed),
    queryFn: withErrorHandling(async (): Promise<CMSContentType[]> => {
      try {
        const raw = await request<any>(
          `${CMS}/content-types${qs({ detailed, include_inactive: includeInactive })}`,
        );
        const list = unwrapList(raw);
        return list.map((t: any) =>
          typeof t === 'string'
            ? { key: t, label: t }
            : { key: t.key, label: t.label ?? t.key, ...t },
        );
      } catch {
        // Fallback built-ins if 0251 missing
        return [
          { key: 'page', label: 'Page' },
          { key: 'article', label: 'Article' },
          { key: 'blog', label: 'Blog' },
          { key: 'news', label: 'News' },
          { key: 'faq', label: 'FAQ' },
          { key: 'legal', label: 'Legal' },
          { key: 'help', label: 'Help' },
          { key: 'announcement', label: 'Announcement' },
          { key: 'category', label: 'Category' },
        ];
      }
    }, 'Failed to fetch content types'),
    staleTime: DEFAULT_STALE_TIME * 12,
    gcTime: DEFAULT_GC_TIME,
  });
};

// ─── Templates ────────────────────────────────────────────────────────────────

export const useCMSTemplatesQuery = (filters: { ctype?: string } = {}) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: cmsKeys.templates(filters),
    queryFn: withErrorHandling(async (): Promise<CMSTemplate[]> => {
      const raw = await request<any>(`${CMS}/templates${qs(filters)}`);
      return (raw?.templates ?? unwrapList(raw)) as CMSTemplate[];
    }, 'Failed to fetch templates'),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

// ─── Field definitions ────────────────────────────────────────────────────────

export const useCMSFieldDefsQuery = (ctype?: string, includeGlobal = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: cmsKeys.fieldDefs(ctype),
    queryFn: withErrorHandling(async (): Promise<CMSFieldDefinition[]> => {
      const raw = await request<any>(
        `${CMS}/fields${qs({ ctype, include_global: includeGlobal })}`,
      );
      return (raw?.fields ?? unwrapList(raw)) as CMSFieldDefinition[];
    }, 'Failed to fetch field definitions'),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

// ─── Tags catalog (focused module; re-exported for stable imports) ────────────
export {
  useAdminTagsQuery,
  useAdminTagDetailQuery,
  useAdminTagUsageQuery,
  useAdminTagEntitiesQuery,
  useAdminTagCmsQuery,
  useEntityTagsQuery,
  tagKeys,
} from '@/client/api/tags/tag.query';

/** @deprecated — variants: filter list by slug client-side via detail/list */
export const useCMSVariantsQuery = (slug: string | null) => {
  const { data, ...rest } = useCMSListQuery(1, { limit: 50 });
  const variants =
    slug && data
      ? data.items.filter((p) => p.slug === slug || p.slug?.startsWith(slug.split('-').slice(0, -1).join('-')))
      : [];
  return { ...rest, data: { status: 200, data: { cms_objects: variants } } };
};
